import { useMemo, useState } from "react";
import { Container, Row, Col, Form, Badge, Alert, Button } from "react-bootstrap";
import Select from "react-select";
import Calendar from "react-calendar";

import CalendarAuditCard from "./CalendarAuditCard";

import "react-calendar/dist/Calendar.css";
import "./CalendarAudits.css";


// >>> НАСТРОЙКА: цвета для полосок многодневных аудитов.
// Каждый аудит получает свой цвет по кругу. Специально без зелёного и серого,
// чтобы не сливалось с точкой-меткой и легендой.
const BAR_COLORS = ["#0d6efd", "#fd7e14", "#6f42c1", "#d63384", "#20c997", "#795548"];

// >>> НАСТРОЙКА: подписи для фильтра "Тип аудита"
const TYPE_OPTIONS = [
  { value: "all", label: "Все" },
  { value: "common", label: "Обычный" },
  { value: "self-esteem", label: "Самоаудит" },
];

// >>> НАСТРОЙКА: подписи для фильтра "Активность"
const ACTIVITY_OPTIONS = [
  { value: "all", label: "Все" },
  { value: "true", label: "Активные" },
  { value: "false", label: "Неактивные" },
];


// ключ дня по локальному времени
const dayKey = (value) => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

// разница в целых днях между двумя датами
const diffInDays = (from, to) => {
  const a = new Date(from.getFullYear(), from.getMonth(), from.getDate());
  const b = new Date(to.getFullYear(), to.getMonth(), to.getDate());
  return Math.round((b - a) / (1000 * 60 * 60 * 24));
};


export default function CalendarAuditsBlock({ facilityData, data }) {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [searchQuery, setSearchQuery] = useState("");
  const [facilityFilter, setFacilityFilter] = useState("all");
  const [activityFilter, setActivityFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");

  const facilityOptions = useMemo(() => {
    const names = (facilityData || []).map((f) => f?.short_name).filter(Boolean);
    return [
      { value: "all", label: "Все заводы" },
      ...names.map((name) => ({ value: name, label: name })),
    ];
  }, [facilityData]);

  const filteredAudits = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return (data || []).filter((item) => {
      if (!item) return false;
      const nameMatch = !query || String(item.name || "").toLowerCase().includes(query);
      const facilityMatch = facilityFilter === "all" || item.facility === facilityFilter;
      const typeMatch = typeFilter === "all" || item.audit_type === typeFilter;
      const activityMatch =
        activityFilter === "all" ||
        (activityFilter === "true" ? item.is_active === true : item.is_active === false);
      return nameMatch && facilityMatch && typeMatch && activityMatch;
    });
  }, [data, searchQuery, facilityFilter, typeFilter, activityFilter]);

  // группируем по дню начала — для точек-счётчиков и списка справа
  const auditsByDay = useMemo(() => {
    const map = new Map();
    filteredAudits.forEach((item) => {
      const key = dayKey(item.start_datetime);
      if (!key) return;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(item);
    });
    return map;
  }, [filteredAudits]);

  // для каждого многодневного аудита: цвет + список дней, которые он покрывает
  const multiDaySpans = useMemo(() => {
    const spans = [];
    let colorIndex = 0;
    filteredAudits.forEach((item) => {
      if (!item?.start_datetime || !item?.end_datetime) return;
      const start = new Date(item.start_datetime);
      const end = new Date(item.end_datetime);
      if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return;
      const totalDays = diffInDays(start, end);
      if (totalDays < 1) return;

      const days = new Set();
      for (let i = 0; i <= totalDays; i++) {
        const d = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i);
        days.add(dayKey(d));
      }
      spans.push({
        id: item.id,
        name: item.name,
        color: BAR_COLORS[colorIndex % BAR_COLORS.length],
        days,
      });
      colorIndex += 1;
    });
    return spans;
  }, [filteredAudits]);

  // словарь "id аудита -> цвет" (только для многодневных); карточка использует его для цветной полоски слева
  const colorByAuditId = useMemo(() => {
    const map = {};
    multiDaySpans.forEach((s) => { map[s.id] = s.color; });
    return map;
  }, [multiDaySpans]);

  const auditsWithoutDate = useMemo(
    () => filteredAudits.filter((item) => dayKey(item.start_datetime) === null),
    [filteredAudits]
  );

  const selectedDayKey = dayKey(selectedDate);

  // аудиты выбранного дня разбиваем на две группы:
  //  - startingToday: у которых start_datetime приходится на выбранный день;
  //  - continuing:    многодневные, которые проходят через день, но начались раньше.
  const { startingToday, continuing } = useMemo(() => {
    const starting = selectedDayKey ? filteredAudits.filter((a) => dayKey(a.start_datetime) === selectedDayKey) : [];
    const startingIds = new Set(starting.map((a) => a.id));
    const cont = selectedDayKey
      ? multiDaySpans
          .filter((s) => s.days.has(selectedDayKey) && !startingIds.has(s.id))
          .map((s) => filteredAudits.find((a) => a.id === s.id))
          .filter(Boolean)
      : [];
    return { startingToday: starting, continuing: cont };
  }, [filteredAudits, multiDaySpans, selectedDayKey]);

  // одна полоска в день: если аудитов несколько — делим её на секции с плавным переходом
  const buildBarBackground = (colors) => {
    if (colors.length === 1) return colors[0];
    const step = 100 / colors.length;
    // >>> НАСТРОЙКА: ширина мягкого перехода между секциями (в процентах ширины полоски)
    const blend = Math.min(4, step / 3);
    const stops = colors.map((color, i) => {
      const from = i === 0 ? 0 : i * step + blend / 2;
      const to = i === colors.length - 1 ? 100 : (i + 1) * step - blend / 2;
      return `${color} ${from}%, ${color} ${to}%`;
    });
    return `linear-gradient(to right, ${stops.join(", ")})`;
  };

  const renderTileContent = ({ date, view }) => {
    if (view !== "month") return null;
    const key = dayKey(date);
    const dayAudits = auditsByDay.get(key);
    const barsForDay = multiDaySpans.filter((s) => s.days.has(key));

    if ((!dayAudits || dayAudits.length === 0) && barsForDay.length === 0) return null;

    const hasArchived = dayAudits && dayAudits.some((a) => a.is_archived === true);

    return (
      <div className="calendar-day-marker">
        {dayAudits && dayAudits.length > 0 && (
          <div className="calendar-day-dotline">
            <span className={`calendar-day-dot ${hasArchived ? "is-archived" : ""}`} />
            <span className="calendar-day-count">{dayAudits.length}</span>
          </div>
        )}
        {barsForDay.length > 0 && (
          <span
            className="calendar-day-bar"
            style={{ background: buildBarBackground(barsForDay.map((s) => s.color)) }}
            title={barsForDay.map((s) => s.name).join(", ")}
          />
        )}
      </div>
    );
  };

  const tileClassName = ({ date, view }) => {
    if (view !== "month") return null;
    const key = dayKey(date);
    const hasAudits = auditsByDay.get(key)?.length > 0;
    const hasBar = multiDaySpans.some((s) => s.days.has(key));
    return hasAudits || hasBar ? "calendar-has-audits" : null;
  };

  const formatSelectedDate = (date) =>
    date.toLocaleDateString("ru-RU", { day: "numeric", month: "long", year: "numeric" });

  const resetFilters = () => {
    setSearchQuery("");
    setFacilityFilter("all");
    setActivityFilter("all");
    setTypeFilter("all");
  };

  return (
    <Container fluid className="calendar-audits">
      <Row className="g-2 mb-3">
        <Col xs={12} md={6} lg={4}>
          <Form.Label className="calendar-filter-label">Поиск по названию</Form.Label>
          <Form.Control
            type="text"
            placeholder="Введите название аудита..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </Col>

        <Col xs={12} md={6} lg={3}>
          <Form.Label className="calendar-filter-label">Завод</Form.Label>
          <Select
            options={facilityOptions}
            value={facilityOptions.find((o) => o.value === facilityFilter) || facilityOptions[0]}
            onChange={(option) => setFacilityFilter(option ? option.value : "all")}
            isSearchable
            placeholder="Завод..."
            classNamePrefix="react-select"
          />
        </Col>

        <Col xs={6} md={6} lg={2}>
          <Form.Label className="calendar-filter-label">Активность</Form.Label>
          <Form.Select value={activityFilter} onChange={(e) => setActivityFilter(e.target.value)}>
            {ACTIVITY_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </Form.Select>
        </Col>

        <Col xs={6} md={6} lg={2}>
          <Form.Label className="calendar-filter-label">Тип</Form.Label>
          <Form.Select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
            {TYPE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </Form.Select>
        </Col>

        <Col xs={12} lg={1} className="d-flex align-items-end">
          <Button variant="outline-secondary" onClick={resetFilters} className="w-100">
            Сброс
          </Button>
        </Col>
      </Row>

      <Row className="mb-2">
        <Col>
          <small className="text-muted">
            Найдено аудитов: <b>{filteredAudits.length}</b>
            {auditsWithoutDate.length > 0 && ` (из них без даты: ${auditsWithoutDate.length})`}
          </small>
        </Col>
      </Row>

      <Row className="g-3">
        <Col xs={12} lg={6}>
          <div className="calendar-wrapper">
            <Calendar
              onChange={setSelectedDate}
              value={selectedDate}
              tileContent={renderTileContent}
              tileClassName={tileClassName}
              locale="ru-RU"
            />
          </div>
          <div className="calendar-legend mt-2">
            <span className="calendar-day-dot" /> есть аудиты&nbsp;&nbsp;
            <span className="calendar-day-dot is-archived" /> есть архивные&nbsp;&nbsp;
            <span className="calendar-legend-bar" /> многодневный аудит
          </div>
        </Col>

        <Col xs={12} lg={6}>
          <h4 className="mb-3">Аудиты на {formatSelectedDate(selectedDate)}</h4>
          {startingToday.length === 0 && continuing.length === 0 ? (
            <Alert variant="secondary">На выбранный день аудитов нет.</Alert>
          ) : (
            <div className="calendar-day-list">
              {startingToday.length > 0 && (
                <>
                  <div className="calendar-day-section-title">Начинаются сегодня</div>
                  {startingToday.map((item) => (
                    <CalendarAuditCard
                      key={item.id}
                      audit={item}
                      accentColor={colorByAuditId[item.id]}
                    />
                  ))}
                </>
              )}
              {continuing.length > 0 && (
                <>
                  <div className="calendar-day-section-title mt-3">Продолжаются</div>
                  {continuing.map((item) => (
                    <CalendarAuditCard
                      key={item.id}
                      audit={item}
                      accentColor={colorByAuditId[item.id]}
                    />
                  ))}
                </>
              )}
            </div>
          )}
        </Col>
      </Row>

      {auditsWithoutDate.length > 0 && (
        <Row className="mt-4">
          <Col>
            <h4 className="mb-3">
              Аудиты без даты начала <Badge bg="secondary">{auditsWithoutDate.length}</Badge>
            </h4>
            {auditsWithoutDate.map((item) => (
              <CalendarAuditCard
                key={item.id}
                audit={item}
                accentColor={colorByAuditId[item.id]}
              />
            ))}
          </Col>
        </Row>
      )}
    </Container>
  );
}
